/**
 * WebSocket handshake authentication (§11.4 of CC_LLD_PRD_0007.md).
 *
 * The Socket.IO `auth` hook acquires an MSAL access token silently for every
 * (re)connect attempt and passes it to the server handshake gate. Refusals
 * come back on `connect_error` with a JSON-encoded SocketAuthRejection body;
 * parseAuthRejection distinguishes them from network failures so the client
 * can trigger a single re-auth redirect instead of socket.io back-off.
 *
 * The codes are not interchangeable: two of them mean "re-authenticate", one
 * means "stop reconnecting", and one means "the server could not decide — try
 * again". Routing a code to the wrong one of those is what turns a transient
 * server fault into a fleet-wide redirect or a permanent lockout.
 */

/** Shape of the auth payload sent to the Socket.IO handshake gate. */
export type SocketAuth = { token: string }

/**
 * Every refusal code the handshake gate can return. Single source for both the
 * type and the guard below — kept as one list because a code present in only
 * one of them parses as a network error, which is the wrong response to a
 * refusal and the wrong error surfaced to the user.
 *
 * auth_unavailable means the server could not *judge* the credential (its
 * datastore was unreachable), not that the credential was rejected — so it is
 * retryable, unlike the three that precede it.
 */
const AUTH_REJECTION_CODES = [
  'auth_required',
  'auth_invalid',
  'authz_denied',
  'auth_unavailable',
] as const

/** Structured body returned by the server on auth-related connect_error. */
export type SocketAuthRejection = { code: (typeof AUTH_REJECTION_CODES)[number] }

/** Type guard — narrows unknown connect_error data to SocketAuthRejection. */
export function isSocketAuthRejection(data: unknown): data is SocketAuthRejection {
  if (typeof data !== 'object' || data === null) return false
  const code = (data as Record<string, unknown>).code
  return AUTH_REJECTION_CODES.includes(code as SocketAuthRejection['code'])
}

/**
 * Parse a connect_error into an auth rejection, or null for network errors.
 * Socket.IO serialises the server-side ConnectionRefusedError body as
 * error.message, so a JSON body with a known code means an auth refusal.
 */
export function parseAuthRejection(error: Error): SocketAuthRejection | null {
  try {
    const parsed: unknown = JSON.parse(error.message)
    return isSocketAuthRejection(parsed) ? parsed : null
  } catch {
    // Not JSON — network error; the caller falls through to normal reconnection.
    return null
  }
}

/**
 * Socket.IO `auth` hook — acquires a fresh MSAL access token for every
 * (re)connect attempt so the handshake gate always sees a valid token.
 * Runs in the socket.io-client auth hook, NOT in the request interceptor —
 * the two token paths are independent.
 */
export async function msalSocketAuth(cb: (auth: SocketAuth) => void): Promise<void> {
  try {
    // Deferred import avoids circular dep at module eval time.
    const { acquireApiToken } = await import('@/services/msal')
    cb({ token: (await acquireApiToken()) ?? '' })
  } catch (err) {
    // Acquisition failed — pass an empty token; the server replies
    // auth_required and the connect_error path triggers re-auth.
    console.warn('WebSocket auth: failed to acquire token silently', err)
    cb({ token: '' })
  }
}

/**
 * Trigger the MSAL re-auth redirect after an auth_required / auth_invalid
 * refusal. Fire-and-forget — the page navigates away on success.
 */
export function redirectForReauth(): void {
  import('@/services/msal')
    .then(({ redirectForApiToken }) => redirectForApiToken())
    .catch((err) => {
      console.error('WebSocket re-auth redirect failed:', err)
    })
}
